import { getRelatedTopics } from '../data';
import type { TopicMetadata } from '../types';

export interface ReadingProgressRecord {
    scrollPercent: number;
    completed: boolean;
    completedAt: string | null;
    lastReadAt: string;
    visitCount: number;
}

export interface SeriesPart {
    slug: string;
    title: string;
    part: number;
}

export interface SeriesProgressInfo {
    name: string;
    currentPart: number;
    totalParts: number;
    parts: SeriesPart[];
}

const STORAGE_KEY_PREFIX = 'bgs:reading-progress:';

function isReadingProgressRecord(value: unknown): value is ReadingProgressRecord {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const record = value as Record<string, unknown>;
    return (
        typeof record.scrollPercent === 'number' &&
        Number.isFinite(record.scrollPercent) &&
        record.scrollPercent >= 0 &&
        record.scrollPercent <= 100 &&
        typeof record.completed === 'boolean' &&
        (record.completedAt === null || typeof record.completedAt === 'string') &&
        typeof record.lastReadAt === 'string' &&
        typeof record.visitCount === 'number' &&
        Number.isFinite(record.visitCount) &&
        record.visitCount >= 0
    );
}

export function readReadingProgress(topicSlug: string): ReadingProgressRecord | null {
    try {
        const raw = window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${topicSlug}`);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        return isReadingProgressRecord(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

export function writeReadingProgress(topicSlug: string, record: ReadingProgressRecord): void {
    try {
        window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${topicSlug}`, JSON.stringify(record));
    } catch {
        // localStorage may be unavailable (private browsing, quota exceeded) - progress simply won't persist.
    }
}

export function createInitialReadingProgress(): ReadingProgressRecord {
    return {
        scrollPercent: 0,
        completed: false,
        completedAt: null,
        lastReadAt: new Date().toISOString(),
        visitCount: 0,
    };
}

export function getSeriesProgressInfo(topic: TopicMetadata): SeriesProgressInfo | null {
    if (!topic.series) {
        return null;
    }

    const siblingParts = getRelatedTopics(topic.slug)
        .filter((related) => related.series?.name === topic.series?.name)
        .map((related) => ({ slug: related.slug, title: related.title, part: related.series?.part ?? 0 }));

    const partsBySlug = new Map<string, SeriesPart>();
    [...siblingParts, { slug: topic.slug, title: topic.title, part: topic.series.part }].forEach((entry) => {
        partsBySlug.set(entry.slug, entry);
    });

    const parts = Array.from(partsBySlug.values()).sort((a, b) => a.part - b.part);

    return {
        name: topic.series.name,
        currentPart: topic.series.part,
        totalParts: topic.series.totalParts,
        parts,
    };
}
